import React, { useRef } from 'react';
import JsBarcode from 'jsbarcode';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import './CarteMembre.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const CarteMembre = ({ membre, onClose }) => {
  const barcodeRef = useRef(null);
  const printRef = useRef(null);

  React.useEffect(() => {
    if (barcodeRef.current && membre.code_barres) {
      try {
        JsBarcode(barcodeRef.current, membre.code_barres, {
          format: 'CODE128',
          width: 2.5,
          height: 100,
          displayValue: true,
          fontSize: 20,
          margin: 8
        });
      } catch (error) {
        console.error('Erreur lors de la génération du code-barres:', error);
      }
    }
  }, [membre.code_barres]);

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [0, 0, 0, 0],
        filename: `Carte_Membre_${membre.numero_matricule || 'MNE'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true,
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: { 
          unit: 'mm', 
          format: [210, 297], // Format A4
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handlePrintCDDVD = async () => {
    if (!printRef.current) return;

    try {
      // Dimensions exactes pour l'impression CD/DVD : 85.6 mm x 53.98 mm
      // Conversion en pixels pour html2canvas (à 96 DPI : 1 mm = 3.779527559 pixels)
      const cardWidthMm = 85.6;
      const cardHeightMm = 53.98;
      const mmToPx = 3.779527559; // Conversion mm vers pixels à 96 DPI
      const cardWidthPx = Math.round(cardWidthMm * mmToPx); // ≈ 323 pixels
      const cardHeightPx = Math.round(cardHeightMm * mmToPx); // ≈ 204 pixels
      
      // Trouver l'élément carte-membre à l'intérieur du conteneur
      const carteElement = printRef.current.querySelector('.carte-membre');
      if (!carteElement) {
        alert('Erreur : Impossible de trouver la carte.');
        return;
      }
      
      // Ajouter la classe spéciale pour ajuster tous les éléments proportionnellement
      carteElement.classList.add('carte-cddvd-size');
      
      // Attendre que les styles soient appliqués
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Utiliser html2canvas pour capturer la carte avec toutes les images
      const canvas = await html2canvas(carteElement, {
        scale: 3, // Scale élevé pour meilleure qualité
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: cardWidthPx,
        height: cardHeightPx,
        allowTaint: true,
        onclone: (clonedDoc) => {
          // S'assurer que le code-barres est visible dans le clone
          const clonedBarcode = clonedDoc.querySelector('.carte-barcode-main');
          if (clonedBarcode && barcodeRef.current) {
            clonedBarcode.innerHTML = barcodeRef.current.innerHTML;
          }
          
          // S'assurer que la classe CD/DVD est appliquée dans le clone
          const clonedCarte = clonedDoc.querySelector('.carte-membre');
          if (clonedCarte) {
            clonedCarte.classList.add('carte-cddvd-size');
          }
        }
      });

      // Retirer la classe spéciale pour restaurer l'affichage normal
      carteElement.classList.remove('carte-cddvd-size');

      // Convertir le canvas en image
      const imageData = canvas.toDataURL('image/png', 1.0);

      // Position pour l'imprimante Epson L805 avec support CD/DVD
      // Selon spécifications Epson L805 : position optimale pour support CD/DVD
      // Format carte CR80 (85.6mm x 53.98mm) sur A4 (210mm x 297mm)
      const topPosition = '71mm'; // Position verticale optimisée pour support CD/DVD Epson L805
      const leftPosition = '31mm'; // Position horizontale optimisée pour support CD/DVD Epson L805
      
      // Dimensions exactes de la carte en mm pour l'affichage
      const cardWidthDisplay = cardWidthMm + 'mm';
      const cardHeightDisplay = cardHeightMm + 'mm';

      // Créer une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert('Erreur : Impossible d\'ouvrir la fenêtre d\'impression. Veuillez autoriser les pop-ups pour ce site.');
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression Carte Membre - CD/DVD</title>
            <style>
              @page {
                size: A4;
                margin: 0;
                padding: 0;
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              
              body {
                margin: 0;
                padding: 0;
                width: 210mm;
                height: 297mm;
                background: white;
                position: relative;
                overflow: hidden;
              }
              
              .print-cddvd-image {
                position: absolute;
                top: ${topPosition};
                left: ${leftPosition};
                width: ${cardWidthDisplay};
                height: ${cardHeightDisplay};
                image-rendering: -webkit-optimize-contrast;
                image-rendering: crisp-edges;
                object-fit: contain;
              }
              
              @media print {
                body {
                  width: 210mm;
                  height: 297mm;
                  margin: 0;
                  padding: 0;
                }
                
                .print-cddvd-image {
                  position: absolute;
                  top: ${topPosition};
                  left: ${leftPosition};
                  width: ${cardWidthDisplay};
                  height: ${cardHeightDisplay};
                  image-rendering: -webkit-optimize-contrast !important;
                  image-rendering: crisp-edges !important;
                }
              }
            </style>
          </head>
          <body>
            <img src="${imageData}" class="print-cddvd-image" alt="Carte de Membre" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 500);
              };
              
              window.onafterprint = function() {
                setTimeout(function() {
                  window.close();
                }, 100);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de l\'impression CD/DVD:', error);
      alert('Erreur lors de l\'impression. Veuillez réessayer.');
    }
  };

  const handlePrintCDDVDL8050 = async () => {
    if (!printRef.current) return;

    try {
      // Dimensions exactes pour l'impression CD/DVD : 85.6 mm x 53.98 mm
      const cardWidthMm = 85.6;
      const cardHeightMm = 53.98;
      const mmToPx = 3.779527559;
      const cardWidthPx = Math.round(cardWidthMm * mmToPx);
      const cardHeightPx = Math.round(cardHeightMm * mmToPx);
      
      const carteElement = printRef.current.querySelector('.carte-membre');
      if (!carteElement) {
        alert('Erreur : Impossible de trouver la carte.');
        return;
      }
      
      carteElement.classList.add('carte-cddvd-size');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const canvas = await html2canvas(carteElement, {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: cardWidthPx,
        height: cardHeightPx,
        allowTaint: true,
        onclone: (clonedDoc) => {
          const clonedBarcode = clonedDoc.querySelector('.carte-barcode-main');
          if (clonedBarcode && barcodeRef.current) {
            clonedBarcode.innerHTML = barcodeRef.current.innerHTML;
          }
          const clonedCarte = clonedDoc.querySelector('.carte-membre');
          if (clonedCarte) {
            clonedCarte.classList.add('carte-cddvd-size');
          }
        }
      });

      carteElement.classList.remove('carte-cddvd-size');
      const imageData = canvas.toDataURL('image/png', 1.0);

      // Position pour l'imprimante Epson L8050 avec support CD/DVD
      // Selon spécifications Epson L8050 : marges minimales 3mm, zone CD/DVD (diamètre 18-120mm)
      // Format carte CR80 (85.6mm x 53.98mm) sur A4 (210mm x 297mm)
      // Carte orientée en PORTRAIT (rotation 90°)
      // Après rotation : dimensions deviennent 53.98mm x 85.6mm
      // Position centrée en portrait : vertical (297-85.6)/2 = 105.7mm, horizontal (210-53.98)/2 = 78.01mm
      const topPosition = '105.7mm'; // Position verticale centrée pour portrait selon spécifications Epson L8050
      const leftPosition = '78.01mm'; // Position horizontale centrée pour portrait selon spécifications Epson L8050
      
      const cardWidthDisplay = cardWidthMm + 'mm';
      const cardHeightDisplay = cardHeightMm + 'mm';

      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert('Erreur : Impossible d\'ouvrir la fenêtre d\'impression. Veuillez autoriser les pop-ups pour ce site.');
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression Carte Membre - CD/DVD (Epson L8050)</title>
            <style>
              @page {
                size: A4;
                margin: 0;
                padding: 0;
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              
              body {
                margin: 0;
                padding: 0;
                width: 210mm;
                height: 297mm;
                background: white;
                position: relative;
                overflow: hidden;
              }
              
              .print-cddvd-image {
                position: absolute;
                top: ${topPosition};
                left: ${leftPosition};
                width: ${cardWidthDisplay};
                height: ${cardHeightDisplay};
                transform: rotate(90deg);
                transform-origin: center center;
                image-rendering: -webkit-optimize-contrast;
                image-rendering: crisp-edges;
              }
              
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                }
                .print-cddvd-image {
                  position: absolute;
                  top: ${topPosition};
                  left: ${leftPosition};
                  width: ${cardWidthDisplay};
                  height: ${cardHeightDisplay};
                  transform: rotate(90deg);
                  transform-origin: center center;
                }
              }
            </style>
          </head>
          <body>
            <img src="${imageData}" class="print-cddvd-image" alt="Carte Membre" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 500);
              };
              
              window.onafterprint = function() {
                setTimeout(function() {
                  window.close();
                }, 100);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de l\'impression CD/DVD L8050:', error);
      alert('Erreur lors de l\'impression. Veuillez réessayer.');
    }
  };

  const handlePrintEvolisPrimacy = async () => {
    if (!printRef.current) return;

    try {
      // Dimensions exactes pour l'impression carte CR80 : 85.6 mm x 53.98 mm
      const cardWidthMm = 85.6;
      const cardHeightMm = 53.98;
      const mmToPx = 3.779527559;
      const cardWidthPx = Math.round(cardWidthMm * mmToPx);
      const cardHeightPx = Math.round(cardHeightMm * mmToPx);
      
      const carteElement = printRef.current.querySelector('.carte-membre');
      if (!carteElement) {
        alert('Erreur : Impossible de trouver la carte.');
        return;
      }
      
      carteElement.classList.add('carte-cddvd-size');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const canvas = await html2canvas(carteElement, {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: cardWidthPx,
        height: cardHeightPx,
        allowTaint: true,
        onclone: (clonedDoc) => {
          const clonedBarcode = clonedDoc.querySelector('.carte-barcode-main');
          if (clonedBarcode && barcodeRef.current) {
            clonedBarcode.innerHTML = barcodeRef.current.innerHTML;
          }
          const clonedCarte = clonedDoc.querySelector('.carte-membre');
          if (clonedCarte) {
            clonedCarte.classList.add('carte-cddvd-size');
          }
        }
      });

      carteElement.classList.remove('carte-cddvd-size');
      const imageData = canvas.toDataURL('image/png', 1.0);

      // Position pour l'imprimante Evolis Primacy
      // Selon spécifications Evolis Primacy : format CR80 (85.6mm x 53.98mm)
      // Impression bord à bord (edge-to-edge) sur cartes PVC - remplissage complet sans marges
      // Format de page personnalisé correspondant exactement à la carte PVC
      const cardWidthExact = '85.6mm'; // Largeur exacte carte CR80
      const cardHeightExact = '53.98mm'; // Hauteur exacte carte CR80
      
      const cardWidthDisplay = cardWidthMm + 'mm';
      const cardHeightDisplay = cardHeightMm + 'mm';

      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert('Erreur : Impossible d\'ouvrir la fenêtre d\'impression. Veuillez autoriser les pop-ups pour ce site.');
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression Carte Membre - Evolis Primacy</title>
            <style>
              @page {
                size: ${cardWidthExact} ${cardHeightExact};
                margin: 0;
                padding: 0;
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
                box-sizing: border-box;
              }
              
              body {
                margin: 0;
                padding: 0;
                width: ${cardWidthExact};
                height: ${cardHeightExact};
                background: white;
                position: relative;
                overflow: hidden;
              }
              
              .print-cddvd-image {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: fill;
                image-rendering: -webkit-optimize-contrast;
                image-rendering: crisp-edges;
                image-rendering: pixelated;
              }
              
              @media print {
                @page {
                  size: ${cardWidthExact} ${cardHeightExact};
                  margin: 0;
                  padding: 0;
                }
                
                body {
                  margin: 0;
                  padding: 0;
                  width: ${cardWidthExact};
                  height: ${cardHeightExact};
                }
                
                .print-cddvd-image {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  object-fit: fill;
                }
              }
            </style>
          </head>
          <body>
            <img src="${imageData}" class="print-cddvd-image" alt="Carte Membre" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 500);
              };
              
              window.onafterprint = function() {
                setTimeout(function() {
                  window.close();
                }, 100);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de l\'impression Evolis Primacy:', error);
      alert('Erreur lors de l\'impression. Veuillez réessayer.');
    }
  };

  if (!membre) return null;

  return (
    <div className="carte-modal-overlay" onClick={onClose}>
      <div className="carte-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="carte-header">
          <h2>Carte de Membre</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="carte-container" ref={printRef}>
          <div className="carte-membre" style={{ '--logo-url': `url(${process.env.PUBLIC_URL || ''}/logo.png)` }}>
            {/* Section supérieure - En-tête bleu */}
            <div className="carte-top-section">
              <div className="carte-top-left">
                <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="Logo MUTU NDE ESPOIR" className="carte-logo-top" />
              </div>
              <div className="carte-top-center">
                <div className="carte-country">RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</div>
                <div className="carte-ministry">MUTU NDE ESPOIR</div>
                <div className="carte-university">ASBL</div>
                <div className="carte-services">Prise en charge santé et sociale</div>
              </div>
              <div className="carte-top-right">
                <img src={`${process.env.PUBLIC_URL || ''}/rdc.png`} alt="Drapeau RDC" className="carte-flag-rdc" />
              </div>
            </div>

            {/* Section centrale - Photo et informations */}
            <div className="carte-middle-section">
              <div className="carte-middle-banner">
                <span>CARTE DE MEMBRE</span>
              </div>
              <div className="carte-middle-content">
                <div className="carte-photo-container">
                  {membre.photo ? (
                    <img 
                      src={`${API_BASE_URL}${membre.photo}`} 
                      alt={`${membre.nom} ${membre.prenom}`}
                      className="carte-photo-main"
                    />
                  ) : (
                    <div className="carte-photo-placeholder-main">
                      <span>Photo</span>
                    </div>
                  )}
                </div>
                <div className="carte-info-container">
                  <div className="carte-info-row carte-matricule-row">
                    <span className="carte-info-label-main carte-matricule-label">MATRICULE:</span>
                    <span className="carte-info-value-main carte-matricule-value">{membre.numero_matricule || '-'}</span>
                  </div>
                  <div className="carte-info-row">
                    <span className="carte-info-label-main">NOM:</span>
                    <span className="carte-info-value-main">{membre.nom}</span>
                  </div>
                  {membre.postnom && (
                    <div className="carte-info-row">
                      <span className="carte-info-label-main">POSTNOM:</span>
                      <span className="carte-info-value-main">{membre.postnom}</span>
                    </div>
                  )}
                  <div className="carte-info-row">
                    <span className="carte-info-label-main">PRÉNOM:</span>
                    <span className="carte-info-value-main">{membre.prenom}</span>
                  </div>
                  {(membre.date_naissance || membre.lieu_naissance) && (
                    <div className="carte-info-row">
                      <span className="carte-info-label-main">NÉ(E) LE:</span>
                      <span className="carte-info-value-main">
                        {membre.date_naissance && new Date(membre.date_naissance).toLocaleDateString('fr-FR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                        {membre.date_naissance && membre.lieu_naissance && ' '}
                        {membre.lieu_naissance && (
                          <>
                            <span style={{ marginLeft: '8px', fontWeight: 700 }}>A:</span> {membre.lieu_naissance}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  {membre.association && (
                    <div className="carte-info-row">
                      <span className="carte-info-label-main">ASSOCIATION:</span>
                      <span className="carte-info-value-main">{membre.association}</span>
                    </div>
                  )}
                  {membre.centre_sante && (
                    <div className="carte-info-row">
                      <span className="carte-info-label-main">CENTRE DE SANTÉ:</span>
                      <span className="carte-info-value-main">{membre.centre_sante}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section inférieure - Date, signature, code-barres */}
            <div className="carte-bottom-section">
              <div className="carte-bottom-left">
                <div className="carte-bottom-text">Fait à Kisangani</div>
                <div className="carte-bottom-date">
                  {new Date(membre.date_adhesion).toLocaleDateString('fr-FR', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric' 
                  })}
                </div>
              </div>
              <div className="carte-bottom-center">
                <div className="carte-signature-area">
                  <div className="carte-signature-line"></div>
                  <div className="carte-signature-name">MUTU NDE ESPOIR</div>
                </div>
              </div>
              <div className="carte-bottom-right">
                {membre.code_barres && (
                <div className="carte-barcode-container">
                  <svg ref={barcodeRef} className="carte-barcode-main"></svg>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="carte-actions">
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-primary" onClick={handlePrintCDDVD}>
            🖨️ Imprimer sur CD/DVD (Epson L805)
          </button>
          <button className="btn btn-primary" onClick={handlePrintCDDVDL8050}>
            🖨️ Imprimer sur CD/DVD (Epson L8050)
          </button>
          <button className="btn btn-primary" onClick={handlePrintEvolisPrimacy}>
            🖨️ Imprimer (Evolis Primacy)
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarteMembre;
